import type {
  GrepFilesArgs,
  GrepFilesResult,
  GrepOutputMode,
} from '../../session';

const VCS_DIRECTORIES_TO_EXCLUDE = [
  '.git',
  '.svn',
  '.hg',
  '.bzr',
  '.jj',
  '.sl',
] as const;

const DEFAULT_HEAD_LIMIT = 250;

export function buildRipgrepArgs(
  args: GrepFilesArgs,
  searchPath: string,
): string[] {
  const mode = args.outputMode ?? 'files_with_matches';
  const result = ['--hidden'];

  for (const dir of VCS_DIRECTORIES_TO_EXCLUDE) {
    result.push('--glob', `!${dir}`);
  }

  result.push('--max-columns', '500');

  if (args.multiline) {
    result.push('-U', '--multiline-dotall');
  }
  if (args.caseInsensitive) {
    result.push('-i');
  }
  if (mode === 'files_with_matches') {
    result.push('-l');
  } else if (mode === 'count') {
    result.push('-c');
  }
  if ((args.showLineNumbers ?? true) && mode === 'content') {
    result.push('-n');
  }

  if (mode === 'content') {
    if (args.context !== undefined) {
      result.push('-C', String(args.context));
    } else {
      if (args.beforeContext !== undefined) {
        result.push('-B', String(args.beforeContext));
      }
      if (args.afterContext !== undefined) {
        result.push('-A', String(args.afterContext));
      }
    }
  }

  if (args.pattern.startsWith('-')) {
    result.push('-e', args.pattern);
  } else {
    result.push(args.pattern);
  }

  if (args.type) {
    result.push('--type', args.type);
  }

  for (const globPattern of splitGrepGlobPatterns(args.glob)) {
    result.push('--glob', globPattern);
  }

  result.push(searchPath);
  return result;
}

export function ripgrepOutputLines(output: string): string[] {
  const normalized = output.replace(/\r\n/gu, '\n').replace(/\r/gu, '\n');
  if (normalized.length === 0) {
    return [];
  }
  const lines = normalized.split('\n');
  if (normalized.endsWith('\n')) {
    lines.pop();
  }
  return lines;
}

export function createGrepFilesResult(
  args: GrepFilesArgs,
  lines: string[],
  displayPath: (path: string) => string,
): GrepFilesResult {
  const mode = args.outputMode ?? 'files_with_matches';
  if (mode === 'content') {
    const { items, appliedLimit } = applyHeadLimit(
      lines,
      args.headLimit,
      args.offset,
    );
    const finalLines = items.map((line) =>
      displayContentLinePath(line, displayPath),
    );
    return {
      mode,
      numFiles: 0,
      filenames: [],
      content: finalLines.join('\n'),
      numLines: finalLines.length,
      ...(appliedLimit !== undefined ? { appliedLimit } : {}),
      ...((args.offset ?? 0) > 0 ? { appliedOffset: args.offset } : {}),
    };
  }

  if (mode === 'count') {
    const { items, appliedLimit } = applyHeadLimit(
      lines,
      args.headLimit,
      args.offset,
    );
    const finalLines = items.map((line) =>
      displayCountLinePath(line, displayPath),
    );
    let numMatches = 0;
    let numFiles = 0;
    for (const line of finalLines) {
      const separator = line.lastIndexOf(':');
      const count =
        separator >= 0 ? Number.parseInt(line.slice(separator + 1), 10) : NaN;
      if (!Number.isNaN(count)) {
        numMatches += count;
        numFiles += 1;
      }
    }
    return {
      mode,
      numFiles,
      filenames: [],
      content: finalLines.join('\n'),
      numMatches,
      ...(appliedLimit !== undefined ? { appliedLimit } : {}),
      ...((args.offset ?? 0) > 0 ? { appliedOffset: args.offset } : {}),
    };
  }

  const { items, appliedLimit } = applyHeadLimit(
    lines,
    args.headLimit,
    args.offset,
  );
  const filenames = items.map(displayPath);
  return {
    mode,
    numFiles: filenames.length,
    filenames,
    ...(appliedLimit !== undefined ? { appliedLimit } : {}),
    ...((args.offset ?? 0) > 0 ? { appliedOffset: args.offset } : {}),
  };
}

function splitGrepGlobPatterns(glob: string | undefined): string[] {
  if (!glob) {
    return [];
  }
  const patterns: string[] = [];
  for (const rawPattern of glob.split(/\s+/u)) {
    if (!rawPattern) {
      continue;
    }
    if (rawPattern.includes('{') && rawPattern.includes('}')) {
      patterns.push(rawPattern);
      continue;
    }
    patterns.push(...rawPattern.split(',').filter(Boolean));
  }
  return patterns;
}

function applyHeadLimit<T>(
  items: T[],
  limit: number | undefined,
  offset = 0,
): { items: T[]; appliedLimit?: number } {
  const safeOffset = Math.max(0, offset);
  if (limit === 0) {
    return { items: items.slice(safeOffset) };
  }

  const safeLimit = Math.max(0, limit ?? DEFAULT_HEAD_LIMIT);
  const sliced = items.slice(safeOffset, safeOffset + safeLimit);
  return {
    items: sliced,
    ...(items.length - safeOffset > safeLimit
      ? { appliedLimit: safeLimit }
      : {}),
  };
}

function displayContentLinePath(
  line: string,
  displayPath: (path: string) => string,
): string {
  const separator = line.indexOf(':');
  if (separator <= 0) {
    return line;
  }
  const path = line.slice(0, separator);
  return `${displayPath(path)}${line.slice(separator)}`;
}

function displayCountLinePath(
  line: string,
  displayPath: (path: string) => string,
): string {
  const separator = line.lastIndexOf(':');
  if (separator <= 0) {
    return line;
  }
  const path = line.slice(0, separator);
  return `${displayPath(path)}${line.slice(separator)}`;
}

export function grepOutputMode(
  value: GrepOutputMode | undefined,
): GrepOutputMode {
  return value ?? 'files_with_matches';
}
